/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Typography, TextField } from "@material-ui/core";
import React, { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { updateLabel, createLabel } from "typescript";
import { updateApplication, createApplication } from "../../../data/operations";
import GreyButton from "../../../elements/GreyButton";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import CustomIcons from "../../../icons/CustomIcons";
import { Application, ApplicationReducer, ApplicationState, UserReducer } from "../../../data/interfaces";
import { useStyles } from "./style";
import { MAX_APPLICATION_DESCRIPTION_LENGTH, MAX_APPLICATION_NAME_LENGTH, PATH_SEPARATOR, RESOURCE_APPLICATIONS, RESOURCE_OVERVIEW } from "../../../data/constants";
import { isInvalidValidScopeName } from "../../../util/token-generation";
import PrimaryButton from "../../../elements/PrimaryButton";
import Settings from "../../../conf/settings";
import { trackApplicationCreation } from "../../../util/app-insights";
import { useHistory } from "react-router-dom";
import { createBrowserHistory } from 'history';
import { connect } from "react-redux";

interface CreateApplicationFormContentProps {

    isEdit: boolean;
    application?: Application;
    redirectToApplication: boolean;

    applicationReducer: ApplicationReducer
    userReducer: UserReducer

    onClose: () => void,
    dispatchCreateApplication: (application: Application) => Promise<string|null>,
    dispatchUpdateApplication: (application: Application) => void,
}

function CreateApplicationFormContent(props: CreateApplicationFormContentProps) {

    const { isEdit, 
            application, 
            redirectToApplication,

            applicationReducer,
            userReducer,
            
            onClose, 
            
            dispatchCreateApplication, 
            dispatchUpdateApplication 
        } = props;
    const classes = useStyles();
    const intl = useIntl();
    const history = useHistory();
    const browserHistory = createBrowserHistory();

    const isInProgress = applicationReducer?.isLoading;
    const appList = applicationReducer?.applications?.list;

    const { selectedOrg } = userReducer;
    const selectedOrgHandleName = selectedOrg.handle;

    const [applicationName, setApplicationName] = useState(isEdit ? application?.name : '');
    const defaultThrottlingPolicy = 'Unlimited';
    const [description, setDescription] = useState(isEdit ? application?.description : '');
    const [applicationNameError, setApplicationNameError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
     const subscriptionScopes = application?.subscriptionScopes ?
            application?.subscriptionScopes.map((scope)=> {return scope.key}) : [];
    const dpSystemApp = application?.attributes?.isDpSystemApp === "true" ? true: false;
    const [isDpSystemApp, setIsDpSystemApp] = useState<boolean>(dpSystemApp);
    const applicationScopes = application?.attributes?.scopes ? 
        (application?.attributes?.scopes as string).split(";") : [];
    const [scopes, setScopes] = useState<string[]>(applicationScopes);

    const emptyApplicationName = intl.formatMessage({
        id: 'application.create.name.empty.helperText',
        defaultMessage: 'Name cannot be empty'
    });

    const invalidApplicationName = intl.formatMessage({
        id: 'application.create.name.invalid.helperText',
        defaultMessage: 'Name contains invalid characters'
    });

    const appNameLimit = intl.formatMessage({
        id: 'application.create.name.limit.helperText',
        defaultMessage: 'Application name must be less than {MAX_APPLICATION_NAME_LENGTH} characters.',
    }, { MAX_APPLICATION_NAME_LENGTH });

    const appNameExists = intl.formatMessage({
        id: 'application.create.name.exists.helperText',
        defaultMessage: 'Application name already exists'
    });

    const appDescriptionLimit = intl.formatMessage({
        id: 'application.create.description.limit.helperText',
        defaultMessage: 'Application description must be less than {MAX_APPLICATION_DESCRIPTION_LENGTH} characters.'
    }, { MAX_APPLICATION_DESCRIPTION_LENGTH });

    const cancelButtonLabel = intl.formatMessage({
        id: 'application.create.cancelButton.label',
        defaultMessage: 'Cancel'
    });
    
    const errorText = scopes.some(isInvalidValidScopeName) ? intl.formatMessage({
        id: 'components.app.CreateApp.create.permission.errorText',
        defaultMessage: 'Invalid Permission(Scope) Name',
    }) : '';

    const createLabel = intl.formatMessage({ id: 'application.create.createButton.label', defaultMessage: 'Create' });

    const updateLabel = intl.formatMessage({ id: 'application.create.updateButton.label', defaultMessage: 'Update' });

    const validateApplicationName = (applicationName: string) => {
        if (applicationName === "") {
            return emptyApplicationName;
        } else if (applicationName.length > MAX_APPLICATION_NAME_LENGTH) {
            return appNameLimit;
        } else if (/([~!@$#;%^&*+=|\\<>"'/,])/.test(applicationName)) {
            return invalidApplicationName;
        } else if (appList?.find((app: Application) => app.name === applicationName)) {
            return appNameExists;
        } else {
            return "";
        }
    };

    const validateDescription = (description: string) => {
        if (description.length > MAX_APPLICATION_DESCRIPTION_LENGTH) {
            return appDescriptionLimit;
        } else {
            return "";
        }
    };

    const createApplication = async () => {
        const newApplication = {
            'name': applicationName,
            'throttlingPolicy': defaultThrottlingPolicy,
            'description': description,
            'tokenType': Settings.defaultAppTokenType,
        };
        const applicationId = await dispatchCreateApplication(newApplication as Application);
        trackApplicationCreation(
            `${applicationId}`,
            `${defaultThrottlingPolicy}`,
            `${description}`
        );
        if (applicationId && redirectToApplication) {
            history.push(PATH_SEPARATOR + selectedOrgHandleName + RESOURCE_APPLICATIONS + PATH_SEPARATOR
                + applicationId + RESOURCE_OVERVIEW);
        }
        onClose();
    };

    const updateApplication = async () => {
        if (application) {
            application.name = applicationName as string;
            application.description = description;
            application.throttlingPolicy = defaultThrottlingPolicy as string;
            application.tokenType = Settings.defaultAppTokenType;
            application.attributes = {
              scopes: scopes.join(";"),
              isDpSystemApp: isDpSystemApp ? "true" : "false",
            };
            await dispatchUpdateApplication(application);
            browserHistory.push('applications');
            onClose();
        }
    };


    const onApplicationChange = (event: ChangeEvent<HTMLInputElement>) => {
        const applicationName = event.target.value;
        setApplicationNameError(validateApplicationName(applicationName));
        setApplicationName(applicationName);
    };

    const onDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        const description = event.target.value;
        setDescriptionError(validateDescription(description));
        setDescription(description);
    };

    const onCancelClick = () => {
        setApplicationNameError('');
        setDescriptionError('');
        setApplicationName('');
        setDescription('');
        setScopes([]);
        onClose();
    };

    if (isInProgress) {
        return (
            <div className={classes.loaderContainer}>
                <Box
                    justifyContent="center"
                    alignItems="center"
                    display="flex"
                    height="50vh"
                    width="50vw"
                >
                    <PreLoaderLogo size="medium" />
                </Box>
            </div>
        );
    } else {
        return (
            <Box
                display="flex"
                flexDirection="column"
                flexWrap="false"
                width={1}
                height={1}
            >
                <div className={classes.formContainer}>
                    <Box
                        justifyContent="space-between"
                        alignItems="flex-start"
                        display="flex"
                        flexDirection="row"
                        my={1}
                        width={1}
                    >
                        <Box
                            justifyContent="space-between"
                            alignItems="flex-start"
                            display="flex"
                            flexDirection="column"
                            my={1}
                            width={1}
                        >
                            <Typography className={classes.textLabelWithAsterisk}>
                                <FormattedMessage id='application.create.name' defaultMessage='Application Name' />
                                <sup className={classes.asterisk}> * </sup>
                            </Typography>
                            <TextField
                                onChange={onApplicationChange}
                                value={applicationName}
                                className={classes.textInput}
                                placeholder="My Sample App"
                                variant="outlined"
                                helperText={ applicationNameError !== "" && (
                                    <div className={classes.helperText}>
                                        <div className={classes.warningIcon}><CustomIcons icon={'warning'} /></div>
                                        <p> {applicationNameError} </p>
                                    </div>
                                )}
                                data-testid="app-name"
                                error={applicationNameError !== ""}
                            />
                        </Box>
                    </Box>
                    <Box
                        justifyContent="space-between"
                        alignItems="flex-start"
                        display="flex"
                        flexDirection="column"
                        my={1}
                        width={1}
                    >
                        <Typography className={classes.textLabel}>
                            <FormattedMessage
                                id='application.create.description'
                                defaultMessage='Application Description'
                            />
                        </Typography>
                        <TextField
                            size="small"
                            onChange={onDescriptionChange}
                            value={description}
                            className={classes.multiLineInput}
                            variant="outlined"
                            multiline={true}
                            rows={5}
                            helperText={descriptionError !== "" && (
                                <div className={classes.helperText}>
                                    <div className={classes.warningIcon}> <CustomIcons icon={'warning'} /> </div>
                                    <p>{descriptionError}</p>
                                </div>
                            )}
                            error={descriptionError !== ""}
                            data-testid="application-description"
                        />
                    </Box>
                    <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                        <GreyButton
                            disabled={isInProgress}
                            onClick={onCancelClick}
                            className={classes.cancelBtn}
                            buttonLabel={cancelButtonLabel}
                        />
                        <PrimaryButton
                            disabled={
                                isInProgress || applicationNameError !== "" || descriptionError !== ""
                                || !applicationName || !!errorText
                            }
                            onClick={isEdit ? updateApplication : createApplication}
                            buttonLabel={isEdit ? updateLabel : createLabel}
                            className={classes.dialogBtn}
                            id="create-button"
                        />
                    </Box>
                </div>
            </Box>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        userReducer: state.userReducer
    };
}

const mapDispatchToProps = {
    dispatchCreateApplication: createApplication,
    dispatchUpdateApplication: updateApplication,
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateApplicationFormContent);
